import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { User, UserRole, Permission, ApiResponse } from '@/types';
import { AuthService } from './auth';

export class UserService {
  private static collection = 'users';

  // List all users (Admin only)
  static async listUsers(
    pageSize: number = 20,
    role?: UserRole
  ): Promise<ApiResponse<User[]>> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser || currentUser.role !== UserRole.ADMIN) {
        throw new Error('Unauthorized: Admin access required');
      }

      let q = query(collection(db, this.collection), orderBy('createdAt', 'desc'));
      
      if (role) {
        q = query(q, where('role', '==', role));
      }

      q = query(q, limit(pageSize));

      const querySnapshot = await getDocs(q);
      const users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as User[];

      return {
        success: true,
        data: users,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to list users',
      };
    }
  }

  // Update user role and permissions (Admin only)
  static async updateUserRole(
    userId: string,
    role: UserRole,
    permissions: Permission[]
  ): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser || currentUser.role !== UserRole.ADMIN) {
        throw new Error('Unauthorized: Admin access required');
      }

      const userRef = doc(db, this.collection, userId);
      await updateDoc(userRef, {
        role,
        permissions,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to update user role: ${error.message || error}`);
    }
  }

  // Deactivate user (Admin only)
  static async setUserStatus(userId: string, status: 'active' | 'inactive'): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser || currentUser.role !== UserRole.ADMIN) {
        throw new Error('Unauthorized: Admin access required');
      }

      const userRef = doc(db, this.collection, userId);
      await updateDoc(userRef, {
        status,
        updatedAt: Timestamp.now(),
      });
    } catch (error: any) {
      throw new Error(`Failed to set user status: ${error.message || error}`);
    }
  }
}
